import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

export interface TradeDetails {
  ticker: string;
  action: string;     // e.g. "Sell PUT", "Sell CALL"
  strike?: number;
  expiry?: string;
  contracts?: number;
  estimatedCredit?: number;
}

interface ExecuteLogPopupProps {
  open: boolean;
  tradeDetails: TradeDetails;
  onClose: () => void;
  onLog: (data: { premiumPerShare: number; openDate: string; notes: string }) => Promise<void>;
}

const ExecuteLogPopup: React.FC<ExecuteLogPopupProps> = ({ open, tradeDetails, onClose, onLog }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [method, setMethod] = useState<'execute' | 'log'>('log');
  const [premium, setPremium] = useState(tradeDetails.estimatedCredit?.toString() ?? '');
  const [openDate, setOpenDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (method === 'execute') return; // Phase 2
    const p = parseFloat(premium);
    if (isNaN(p) || p <= 0) {
      setError('Enter a valid premium per share');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onLog({ premiumPerShare: p, openDate, notes });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to log trade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle>
        {tradeDetails.action} — {tradeDetails.ticker}
      </DialogTitle>

      <DialogContent>
        {/* Trade summary */}
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 2 }}>
          <Typography variant="body2">
            {tradeDetails.strike && `Strike: $${tradeDetails.strike}  `}
            {tradeDetails.expiry && `Expiry: ${tradeDetails.expiry}  `}
            {tradeDetails.contracts && `Contracts: ${tradeDetails.contracts}`}
          </Typography>
          {tradeDetails.estimatedCredit && (
            <Typography variant="body2">
              Est. credit: ${tradeDetails.estimatedCredit.toFixed(2)}/share
            </Typography>
          )}
        </Box>

        <RadioGroup value={method} onChange={(e) => setMethod(e.target.value as 'execute' | 'log')} row={!fullScreen}>
          <FormControlLabel
            value="execute"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Execute Trade</Typography>
                <Typography variant="caption" color="text.secondary">via Tradier</Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="log"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Log Trade</Typography>
                <Typography variant="caption" color="text.secondary">manual entry</Typography>
              </Box>
            }
          />
        </RadioGroup>

        <Divider sx={{ my: 2 }} />

        {method === 'execute' ? (
          <Alert severity="info">
            Broker API execution is not yet available. Use <strong>Log Trade</strong> to manually record this position.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Premium received (per share)"
              type="number"
              value={premium}
              onChange={(e) => setPremium(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              size="small"
              required
            />
            <TextField
              label="Open date"
              type="date"
              value={openDate}
              onChange={(e) => setOpenDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              size="small"
            />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || method === 'execute'}
        >
          {loading ? 'Saving…' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExecuteLogPopup;
